/** @jsx jsx */
import { useStaticQuery, graphql } from 'gatsby';
import get from 'lodash/get';
import { Footer as BaseFooter, jsx } from 'theme-ui';
import { Col, Container, Row } from '../Grid';
import * as styles from './Footer.Basic.styles';
import Image from 'gatsby-image';
import FooterNavItem from '@fify.at/gatsby-theme-base/src/components/Footer/FooterNavItem';
import SocialMedia from '@fify.at/gatsby-theme-base/src/components/SocialMedia/SocialMedia';
import React from 'react';

/**
 * This is a simple footer component intented to be styled by using the CSS grid system.
 */
export default function FooterBasic({ lang, fluid = false, ...rest }) {
  const {
    navigation,
    contact,
    showSocialMedia,
    logo,
    firmName,
    firmDescription,
  } = useFooterQuery({
    lang,
  });
  const siteTitle = 'footerLogo';

  return (
    <BaseFooter
      sx={{ ...styles.Footer, variant: 'layout.Footer.Footer' }}
      {...rest}
    >
      <Container
        fluid={fluid}
        sx={{ ...styles.Container, variant: 'layout.Footer.Container' }}
      >
        <Row
          sx={{
            ...styles.InformationRow,
            variant: 'layout.Footer.InformationRow',
          }}
        >
          <Col md={3}>
            {/*<h4 sx={{ ...styles.Title, variant: 'layout.Footer.Title' }}>Über uns</h4>*/}
            {logo && logo.childImageSharp && logo.childImageSharp.fluid && (
              <Image
                imgStyle={{
                  objectFit: 'contain',
                }}
                sx={{ ...styles.Logo, variant: 'layout.Footer.Logo' }}
                fluid={logo.childImageSharp.fluid}
                alt={siteTitle}
              />
            )}
            {firmDescription && (
              <div dangerouslySetInnerHTML={{ __html: firmDescription }} />
            )}
          </Col>
          <Col md={3}>
            <h4 sx={{ ...styles.Title, variant: 'layout.Footer.Title' }}>
              Informationen
            </h4>
            {contact.show && (
              <>
                <address
                  sx={{ ...styles.Address, variant: 'layout.Footer.Address' }}
                >
                  {contact.address && contact.address.length && (
                    <p>
                      {contact.address.map((row, i) => (
                        <>
                          {' '}
                          <span dangerouslySetInnerHTML={{ __html: row }} />
                          {i + 1 < contact.address.length && <br />}
                        </>
                      ))}
                    </p>
                  )}
                </address>
                {(contact.email.length > 0 ||
                  contact.tel.length > 0 ||
                  contact.fax.length > 0) && (
                  <address
                    sx={{ ...styles.Address, variant: 'layout.Footer.Address' }}
                  >
                    {contact.email && (
                      <>
                        E-Mail:{' '}
                        <a href={`mailto:${contact.email}`}>{contact.email}</a>
                        {(contact.fax.length || contact.tel.length) && <br />}
                      </>
                    )}
                    {contact.tel && (
                      <>
                        Telefon:{' '}
                        <a href={`tel:${contact.tel}`}>{contact.tel}</a>
                        {contact.fax.length > 0 ? <br /> : undefined}
                      </>
                    )}
                    {contact.fax.length > 0 ? (
                      <>Fax: ${contact.fax}</>
                    ) : (
                      undefined
                    )}
                  </address>
                )}
              </>
            )}
          </Col>
          <Col md={3}>
            <h4 sx={{ ...styles.Title, variant: 'layout.Footer.Title' }}>
              Links
            </h4>
            {navigation && (
              <nav
                sx={{
                  ...styles.FooterNavigation,
                  variant: 'layout.Footer.Navigation',
                }}
              >
                <ul>
                  {navigation.map(item => (
                    <FooterNavItem
                      sx={styles.FooterNavItem}
                      key={item.slug || item.url}
                      {...item}
                    />
                  ))}
                </ul>
              </nav>
            )}
          </Col>
          <Col md={3}>
            <h4 sx={{ ...styles.Title, variant: 'layout.Footer.Title' }}>
              Folge uns
            </h4>
            {showSocialMedia && <SocialMedia />}
          </Col>
        </Row>
      </Container>
      <Container
        fluid={fluid}
        sx={{ ...styles.Container, variant: 'layout.Footer.Container' }}
      >
        <Row
          sx={{ ...styles.CopyrightRow, variant: 'layout.Footer.CopyrightRow' }}
        >
          <Col>
            <div
              sx={{
                ...styles.Container.Copyright,
                variant: 'layout.Footer.Copyright',
              }}
            >
              © 2020 Copyright: {firmName && firmName}
            </div>
          </Col>
        </Row>
      </Container>
    </BaseFooter>
  );
}

/**
 * Extract the data for the header from the site metadata (defined in `gatsby-config.js`).
 * @param {Object} props The configuration.
 * @param {String} props.lang The language.
 * @returns {{
 *      logo: {src: String, srcSet: String}
 *    }} The navigation and logo for the footer.
 */
export function useFooterQuery({ lang }) {
  const data = useStaticQuery(graphql`
    {
      file(name: { eq: "logo" }) {
        childImageSharp {
          fixed(width: 200) {
            ...GatsbyImageSharpFixed_withWebp_tracedSVG
          }
        }
      }
      site {
        siteMetadata {
          footer {
            navigation {
              lang
              slug
              title
              url
            }
            showSocialMedia
            map {
              show
              query
              center
            }
            contact {
              show
              address
              tel
              fax
              email
            }
            firmName
            firmDescription
            logo {
              childImageSharp {
                fluid(quality: 95) {
                  ...GatsbyImageSharpFluid_withWebp_tracedSVG
                }
              }
            }
          }
        }
      }
    }
  `);
  const {
    file,
    site: {
      siteMetadata: { footer },
    },
  } = data;
  // Extract the navigation lists.
  const isCorrectLang = item => (lang ? item.lang && item.lang === lang : true);
  const navigation =
    footer && footer.navigation.filter(item => isCorrectLang(item));
  return {
    navigation,
    contact: footer.contact,
    showSocialMedia: footer.showSocialMedia,
    firmName: footer.firmName,
    firmDescription: footer.firmDescription,
    map: footer.map,
    logo: footer.logo,
  };
}
